import * as yup from "yup";
import {
  Property,
  PropertyType,
  PropertyVisibility,
  ProperyEditability,
} from "../../../../gql/graphql";
import { useEffect, useState } from "react";
import {
  Button,
  Group,
  Select,
  Stack,
  Switch,
  TextInput,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import type { DropResult } from "@hello-pangea/dnd";
import { EmptyState } from "../EmptyState";
import { CustomFieldsTable } from "../CustomFieldsTable";
import type { CustomFieldRow } from "../CustomFieldsTable";
import { useConfirmModal } from "../ConfirmModal";

export const yupConfig = yup
  .array()
  .of(
    yup.object().shape({
      name: yup.string().required(),
      order: yup.number().required(),
      property: yup.string().required(),
      editability: yup
        .mixed()
        .oneOf(Object.values(ProperyEditability))
        .required(),
      visibility: yup
        .mixed()
        .oneOf(Object.values(PropertyVisibility))
        .required(),

      required: yup.boolean().required(),
      type: yup.string().required(),
      unique: yup.boolean().required(),
    }),
  )
  .required();

interface EnumOption<T extends string> {
  value: T;
  label: T;
}

const enumOptions = <T extends string>(
  enumObj: Record<string, T>,
): EnumOption<T>[] =>
  Object.values(enumObj).map((value) => ({ value, label: value }));

const propertyTypeOptions = enumOptions(PropertyType);
const propertyVisibilityOptions = enumOptions(PropertyVisibility);
const propertyEditabilityOptions = enumOptions(ProperyEditability);

const isPropertyType = (value: string | null): value is PropertyType =>
  value !== null && (Object.values(PropertyType) as string[]).includes(value);

const isPropertyVisibility = (
  value: string | null,
): value is PropertyVisibility =>
  value !== null &&
  (Object.values(PropertyVisibility) as string[]).includes(value);

const isProperyEditability = (
  value: string | null,
): value is ProperyEditability =>
  value !== null &&
  (Object.values(ProperyEditability) as string[]).includes(value);

const createDefaultField = (): Property => ({
  name: "",
  order: 0,
  property: "",
  visibility: PropertyVisibility.Public,
  editability: ProperyEditability.Always,
  required: false,
  type: PropertyType.String,
  unique: false,
});

const normalizeDefaultValue = (
  defaultValue?: Array<Property> | null,
): Property[] =>
  defaultValue?.map(
    ({ __typename: _typename, public: isPublic, ...rest }) => {
      const visibility =
        isPublic === false
          ? PropertyVisibility.OwnerOrPermission
          : rest.visibility || PropertyVisibility.Public;
      const editability = rest.editability || ProperyEditability.Always;
      return {
        ...rest,
        visibility,
        editability,
      };
    },
  ) || [];

const toTableRows = (fields: Property[]): CustomFieldRow[] =>
  fields.map((field, index) => ({
    id: field.property || `new-field-${index}`,
    name: field.name || "Untitled",
    propertyKey: field.property,
    type: field.type,
    required: field.required,
    unique: field.unique,
    visibility: field.visibility ?? PropertyVisibility.Public,
    editability: field.editability ?? ProperyEditability.Always,
  }));

interface CustomFieldEditorProps {
  initialField: Property;
  isEditing: boolean;
  onSave: (field: Property) => void;
  onCancel: () => void;
}

const CustomFieldEditor = ({
  initialField,
  isEditing,
  onSave,
  onCancel,
}: CustomFieldEditorProps) => {
  const [field, setField] = useState<Property>(initialField);

  const updateField = (updates: Partial<Property>) => {
    setField((prev) => ({ ...prev, ...updates }));
  };

  return (
    <Stack gap="sm">
      <TextInput
        label="Name"
        placeholder="My Field Name"
        value={field.name}
        onChange={(e) => updateField({ name: e.currentTarget.value })}
        required
      />
      <TextInput
        label="Property Key"
        placeholder="my_field.property"
        value={field.property}
        onChange={(e) => updateField({ property: e.currentTarget.value })}
        disabled={isEditing}
        required
      />
      <Select
        label="Type"
        data={propertyTypeOptions}
        value={field.type}
        onChange={(val) =>
          updateField({
            type: isPropertyType(val) ? val : PropertyType.String,
          })
        }
      />
      <Select
        label="Visibility"
        data={propertyVisibilityOptions}
        value={field.visibility ?? PropertyVisibility.Public}
        onChange={(val) =>
          updateField({
            visibility: isPropertyVisibility(val)
              ? val
              : PropertyVisibility.Public,
          })
        }
      />
      <Select
        label="Editability"
        data={propertyEditabilityOptions}
        value={field.editability ?? ProperyEditability.Always}
        onChange={(val) =>
          updateField({
            editability: isProperyEditability(val)
              ? val
              : ProperyEditability.Always,
          })
        }
      />
      <Group gap="xl" mt="xs">
        <Switch
          label="Required"
          checked={field.required}
          onChange={(e) => updateField({ required: e.currentTarget.checked })}
        />
        <Switch
          label="Unique"
          checked={field.unique}
          onChange={(e) => updateField({ unique: e.currentTarget.checked })}
        />
      </Group>
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="cyan"
          onClick={() => onSave(field)}
          disabled={!field.name || !field.property}
        >
          {isEditing ? "Save Changes" : "Add Field"}
        </Button>
      </Group>
    </Stack>
  );
};

type CustomFieldsOnChange = (...newValues: Array<Property[]>) => void;

interface CustomFieldsFormProps {
  onChange: CustomFieldsOnChange;
  defaultValue?: Array<Property> | null;
  buttonLabel?: string;
}

export const CustomFieldsForm = ({
  onChange,
  defaultValue,
  buttonLabel = "Add custom field",
}: CustomFieldsFormProps) => {
  const [customFields, setCustomFields] = useState<Property[]>(() =>
    normalizeDefaultValue(defaultValue),
  );
  const { confirm } = useConfirmModal();

  useEffect(() => {
    onChange(customFields);
    // onChange is intentionally omitted: callers frequently pass inline
    // closures, and re-emitting when only the identity changes would
    // trigger redundant form updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields]);

  const openFieldEditor = (editIndex: number | null) => {
    const isEditing = editIndex !== null;
    const initialField = isEditing
      ? { ...customFields[editIndex] }
      : createDefaultField();
    const modalId = `custom-field-editor-${isEditing ? editIndex : "new"}`;

    modals.open({
      modalId,
      title: isEditing ? "Edit Custom Field" : "Add Custom Field",
      size: "md",
      children: (
        <CustomFieldEditor
          initialField={initialField}
          isEditing={isEditing}
          onSave={(field) => {
            if (isEditing) {
              setCustomFields((prev) =>
                prev.map((f, i) => (i === editIndex ? { ...field } : f)),
              );
            } else {
              setCustomFields((prev) => [
                ...prev,
                { ...field, order: prev.length },
              ]);
            }
            modals.close(modalId);
          }}
          onCancel={() => modals.close(modalId)}
        />
      ),
    });
  };

  const handleDelete = async (index: number, name: string) => {
    const confirmed = await confirm({
      title: "Delete Custom Field",
      message: `Are you sure you want to delete the field "${name}"?`,
      confirmLabel: "Delete",
      type: "danger",
    });
    if (!confirmed) return;
    setCustomFields((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((f, i) => ({ ...f, order: i }));
    });
  };

  const handleDragEnd = (result: DropResult) => {
    const { destination } = result;
    if (!destination) return;

    setCustomFields((prev) => {
      const items = [...prev];
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      return items.map((f, i) => ({ ...f, order: i }));
    });
  };

  const tableRows = toTableRows(customFields);

  return (
    <>
      <Button
        variant="light"
        color="cyan"
        leftSection={<IconPlus size={16} />}
        onClick={() => openFieldEditor(null)}
        mb="sm"
      >
        {buttonLabel}
      </Button>

      {customFields.length === 0 ? (
        <EmptyState
          compact
          title="No custom fields"
          description="Add custom fields to extend your entity with additional properties."
        />
      ) : (
        <CustomFieldsTable
          fields={tableRows}
          onDragEnd={handleDragEnd}
          onEdit={openFieldEditor}
          onDelete={handleDelete}
          droppableId="custom-fields"
        />
      )}
    </>
  );
};

export const constructCustomFieldsForm = ({
  defaultValue,
  label,
}: {
  defaultValue?: Array<Property> | null;
  label: string;
}) => ({
  type: "DYNAMIC" as const,
  label,
  yupConfig,
  defaultValue: defaultValue ?? [],
  element: (onChange: CustomFieldsOnChange) => (
    <CustomFieldsForm onChange={onChange} defaultValue={defaultValue} />
  ),
});
